import noUiSlider from 'nouislider'
import { Controller } from 'stimulus'
import './slider_component.scss'

export default class extends Controller {
  static targets = ['min', 'max', 'range']
  static values = {
    currentMin: Number,
    currentMax: Number,
    totalMin: Number,
    totalMax: Number,
    formattingOptions: Object
  }

  connect = (): void => {
    if (this.mySlider !== undefined) return
    this.updateInputs([this.currentMinValue, this.currentMaxValue])
    const step = this.sensibleStep(this.currentMinValue, this.currentMaxValue)

    this.mySlider = noUiSlider.create(this.rangeTarget, {
      start: [this.currentMinValue, this.currentMaxValue],
      connect: true,
      tooltips: true,
      step,
      format: {
        to: this.formatNumber,
        from: this.deformatNumber
      },
      range: {
        min: this.totalMinValue,
        max: this.totalMaxValue
      },
      pips: {
        mode: 'positions',
        values: [0, 100],
        density: 100
      }
    })

    this.mySlider.on('change', (newValues, handle) => {
      if (this.mySlider.getPositions()[0] === 0) newValues[0] = this.totalMinValue
      if (this.mySlider.getPositions()[1] === 100) newValues[1] = this.totalMaxValue
      this.updateInputs(newValues)
      this.updatePanel()
    })

    this.updatePanel()
  }

  updateInputs = (values: array): void => {
    const deformattedValues = values.map(this.deformatNumber)
    this.minInput = deformattedValues[0]
    this.maxInput = deformattedValues[1]
  }

  updateSlider = (event: object): void => {
    this.minSlider = this.minInput
    this.maxSlider = this.maxInput
  }

  updatePanel = (): void => {
    const active: boolean =
      this.minInput > this.totalMinValue || this.maxInput < this.totalMaxValue
    if (active) {
      this.menuPanelController().activate()
    } else {
      this.menuPanelController().deactivate()
      this.minInput = ''
      this.maxInput = ''
    }
  }

  menuPanelController = (): object => {
    return this.application.getControllerForElementAndIdentifier(
      this.element.parentNode.parentNode,
      'menu-panel'
    )
  }

  reset = (): void => {
    this.mySlider.set([this.totalMinValue, null])
    this.mySlider.set([null, this.totalMaxValue])
    this.minInput = ''
    this.maxInput = ''
    this.updatePanel()
    this.navigationController.clearQueryParams()
  }

  valid = (value: number): boolean => {
    return (
      typeof value === 'number' &&
      this.totalMinValue <= value &&
      value <= this.totalMaxValue
    )
  }

  sensibleStep = (minValue: number, maxValue: number): number => {
    const rangeVal = maxValue - minValue

    if (rangeVal === 0) {
      return 1
    }

    const rawStep = rangeVal / 10.0
    const multiplier = 10 ** (Math.floor(Math.log10(rawStep)))
    const step = Math.round(rawStep / multiplier) * multiplier

    return step === 0 ? 1 : step
  }

  get minInput (): number {
    return parseFloat(this.minTarget.value)
  }

  set minInput (value: number) {
    const parsedValue = parseFloat(value)
    if (this.valid(parsedValue)) {
      this.minTarget.value = parsedValue
    } else {
      this.minTarget.value = ''
    }
  }

  get maxInput (): number {
    return parseFloat(this.maxTarget.value)
  }

  set maxInput (value: number) {
    const parsedValue = parseFloat(value)
    if (this.valid(parsedValue)) {
      this.maxTarget.value = parsedValue
    } else {
      this.maxTarget.value = ''
    }
  }

  get minSlider (): number {
    return parseFloat(this.mySlider.get()[0])
  }

  set minSlider (value: number) {
    const parsedValue = parseFloat(value)
    if (this.valid(parsedValue)) {
      this.mySlider.set([parsedValue, null])
    } else {
      this.mySlider.set([this.totalMinValue, null])
    }
  }

  get maxSlider (): number {
    return parseFloat(this.mySlider.get()[1])
  }

  set maxSlider (value: number) {
    const parsedValue = parseFloat(value)
    if (this.valid(parsedValue)) {
      this.mySlider.set([null, parsedValue])
    } else {
      this.mySlider.set([null, this.totalMaxValue])
    }
  }

  get navigationController (): object {
    return this.application.getControllerForElementAndIdentifier(
      this.element.closest('.navigation'),
      'navigation'
    )
  }

  submitForm = (event): void => {
    event.preventDefault()
    this.navigationController.submitForm()
  }

  formatNumber = (value: number): string => {
    let formattedValue = String(value)

    if (this.formattingOptionsValue.delimiter === true) {
      formattedValue = this.formatWithDelimiter(value)
    } else if (this.formattingOptionsValue.abbreviation === true) {
      formattedValue = this.formatNumberinAbbreviation(value)
    }

    if (this.formattingOptionsValue.unit !== '') {
      if (this.formattingOptionsValue.suffix === true) {
        formattedValue = `${formattedValue}${this.formattingOptionsValue.unit as string}`
      } else {
        formattedValue = `${this.formattingOptionsValue.unit as string}${formattedValue}`
      }
    }
    return formattedValue
  }

  deformatNumber = (value: string | number): number => {
    if (typeof value === 'number') return value
    let number = 0
    if (this.formattingOptionsValue.unit !== '') {
      value = value.replace(this.formattingOptionsValue.unit, '')
    }

    if (this.formattingOptionsValue.delimiter === true) {
      number = this.delimiterToNumber(value)
    } else if (this.formattingOptionsValue.abbreviation === true) {
      number = this.decodeFormatNumberinAbbreviation(value)
    }
    return number
  }

  formatWithDelimiter = (value: number): string => {
    return value.toString().replace(/\B(?=(\d{3})+(?!\d))/g, ',')
  }

  delimiterToNumber = (value: string): number => {
    return parseFloat(value.replace(/,/g, ''))
  }

  formatNumberinAbbreviation = (value): string => {
    if (value < 1e3) {
      return value.toFixed(0)
    } else if (value < 1e6) {
      return (value / 1e3).toFixed(0) + 'k'
    } else if (value < 1e9) {
      return (value / 1e6).toFixed(0) + 'M'
    } else if (value < 1e12) {
      return (value / 1e9).toFixed(0) + 'B'
    } else {
      return (value / 1e12).toFixed(0) + 'T'
    }
  }

  focus = (): void => {
    this.minTarget.focus()
  }

  decodeFormatNumberinAbbreviation (formattedValue): number {
    const numericValue = parseFloat(formattedValue.replace(/[,kMBT]/g, ''))
    const unit = formattedValue.slice(-1)

    switch (unit) {
      case 'k':
        return numericValue * 1e3
      case 'M':
        return numericValue * 1e6
      case 'B':
        return numericValue * 1e9
      case 'T':
        return numericValue * 1e12
      default:
        return numericValue
    }
  }
}
